/**
 * Retry for a callable whose request never got an answer (the browser's "status 0").
 *
 * WHY. Sentry OFFICEHUB-CLIENT-63 (2026-10): real staff on phones and laptops hit status-0
 * failures across 24 different callables. The Firebase SDK swallows the rejected fetch as
 * "internal [0]" and never retries, so one dropped connection became a visible failure.
 * Server side, the same callables returned 0 server errors in 7 days, so these are lost
 * requests, not server faults.
 *
 * WHAT IS SAFE. A status-0 failure does not tell us whether the server ran the call (the
 * response, not the request, may have been lost). So a retry is only allowed where running
 * the call twice is harmless, and the default is NO retry:
 *   - read-only callables, and callables whose only write is idempotent bookkeeping or a keyed replay (each entry below carries
 *     its file:line proof; src/lib/__tests__/callableRetry.test.ts pins the exact set);
 *   - `forms_createSubmission` ONLY for a SUBMIT carrying a client-minted `submissionId`: the server
 *     accepts a replayed submit into the already-submitted document by design. NOT autosave ticks (a
 *     replay is refused as stale and the draft-sync loop then stops backing the report up for the
 *     session) and NOT Save Draft (a replay bumps `explicitSaveCount` and can land behind a newer
 *     save). Without a submissionId the server mints the id and a retry would create a second report.
 * Anything not listed here is attempted exactly once.
 *
 * WHAT IS NEVER RETRIED: an HTTP response of any status (the server received the request), an
 * AbortError or already-aborted signal (the caller or a navigation cancelled it), a device that
 * reports offline (the offline queue owns that case), a non-POST, a host other than our own
 * callable host, or a body we cannot inspect.
 *
 * A SLOW BOOT IS HEDGED, NOT WAITED OUT (error group f8f7e3503c27e2cb, 2026-10-29). For the QuickBooks read family only
 * (HEDGED_CALLABLES), an attempt that has not answered after HEDGE_AFTER_MS gets ONE fresh twin, and the first answer wins.
 * See fetchWithHedge for the measurements.
 */

// `<region>-quoter-saas`: exactly one region token (e.g. us-central1), so another project whose id merely
// ends in -quoter-saas (`us-central1-foo-quoter-saas`) is not ours.
const CALLABLE_URL_RE = /^https:\/\/[a-z]+-[a-z]+[0-9]+-quoter-saas\.cloudfunctions\.net\/([A-Za-z0-9_]+)(?:[?#].*)?$/;

type CallableData = Record<string, unknown>;

/**
 * callable name -> "is a repeat of THIS call harmless?". Each entry was verified against its
 * server source: the pure reads perform no writes; getExecutiveFinancials is idempotent rather than
 * write-free, and forms_createSubmission is a keyed upsert (both noted below).
 */
const RETRY_SAFE: Readonly<Record<string, (data: CallableData) => boolean>> = {
  // Mint a scoped Typesense search key: pure read.
  search_getScopedSearchKey: () => true,
  // "What is the next move on this ticket": documented as performing NO writes.
  fieldService_ticketNextStep: () => true,
  // Tickets billed in the last few days, from the audit trail: documented as performing NO writes.
  fieldService_recentlyBilled: () => true,
  fieldService_listGenerators: () => true,
  projects_listAssignableEmployees: () => true,
  // Added after #6299 went live: still recurring in CLIENT-63. Each is a query plus a filter in
  // memory (installInvoiceReadService.listInstallInvoices, driverPortal getEligibility, the
  // connectionSummaries handler); none writes or counts, and their auth wrappers only read the caller's
  // users doc (and Firebase Auth for a display name). search_searchRestricted is NOT here: every
  // call spends the caller's role-gated search budget.
  projects_listInstallInvoices: () => true,
  driver_getMyVehicleEligibility: () => true,
  // Sentry OFFICEHUB-CLIENT-63, 2026-10: lost requests on two more pure reads, read from the server source on origin/main (not run).
  //  - fieldService_listPendingGeneratorContacts: the contacts still SAVING on one unit; the service (projects/contactAttachQueued.ts
  //    listPendingGeneratorContacts) is one Firestore query with .get() plus an in-memory sort, and the open list polls it every few
  //    seconds by design, so a repeat is already the normal load.
  //  - driver_getMyVehicles: the driver's own vehicles (fleet/driverPortal/service.ts getMyVehicles): equality queries with .get(), and a
  //    signed READ url for a document (a local signature, stores nothing). No write, no counter.
  // Both sit behind the same auth wrappers as the entries above (a read of the caller's users doc, then the handler); the wrappers add
  // only request telemetry. NOT added: drive_listFolderContents, whose Drive client refreshes an expired token and UPDATES the connection
  // document (idempotent, but a write), so it is not proven write-free.
  fieldService_listPendingGeneratorContacts: () => true,
  driver_getMyVehicles: () => true,
  dataHub_listConnectionSummaries: () => true,
  // Dropdown options for a form field. Verified on the server source: every data source under
  // forms/dataSources is a query plus a projection (no write); the only write is the per-user rate-limit
  // counter (30/min, about one call per preset per 5 minutes), which a retry cannot exhaust. A cold
  // CORS preflight (~20 s measured 2026-10-12) is the usual reason the first attempt gets no answer.
  forms_getDataSourceOptions: () => true,
  // Sentry OFFICEHUB-CLIENT-63 (2026-11-03): two more lost requests on forms reads, read from the server source on origin/main (not run).
  //  - forms_getBlockingDrafts: getBlockingDraftsForUser (forms/blockingDrafts.ts) reads the enforce switch, the caller's drafts, the ticket
  //    statuses and each ticket's report assessment ("3-6 reads" per ticket in its own comment); the file holds no Firestore write
  //    (its only `.set(` is a JavaScript Map). Measured: its OPTIONS preflight is the slowest user-facing forms preflight (a cold
  //    start of ~19 s on 2026-11-03 00:58Z), the usual reason the first attempt gets no answer on a phone.
  //  - forms_listKeptAside: listKeptAside (forms-module/services/keptAsideReadService.ts) is four reads (the caller's users and members docs,
  //    the submission, then the kept-aside page) and no write. The one write on the path is the per-user read counter (60/min,
  //    FORMS_KEPT_ASIDE_RATE_LIMIT, written before the handler runs, so a lost request may already have spent one); a retry adds at most
  //    two more per lost call, which a single-submission read cannot reach 60 with.
  forms_getBlockingDrafts: () => true,
  forms_listKeptAside: () => true,
  // The QuickBooks read family (#6381): ONE client (lib/qbo/qboReadClient.ts) and ONE server folder
  // (functions/src/qboRead/). Read from source on origin/main (not run): none of the 16 files in that folder holds a
  // Firestore write pattern (the same check on a known writer finds 44), the handler only logs the op and the org, and a
  // paged read repeats with the same cursor. A lost request on the support inbox's billing box (support_getRelatedBilling)
  // was the newest Sentry OFFICEHUB-CLIENT-69 'internal [0]' event (2026-10-14 00:33Z, a fifth user). The whole family
  // is listed because the shared client cannot tell the callers apart and they differ only by which tables they read.
  support_getRelatedBilling: () => true,
  finance_qboRead: () => true,
  projects_qboRead: () => true,
  fieldService_qboRead: () => true,
  dataHub_readQboTable: () => true,
  customers_qboRead: () => true,
  // Recomputes a customer's `hasUnsentInvoices` flag from the CURRENT invoices and sent records and writes it only when it
  // differs (collectionsService.updateHasUnsentInvoices): a derived value, so a repeat leaves the same state. A lost request
  // was Sentry OFFICEHUB-CLIENT-69 on 2026-09-23.
  collections_updateHasUnsentInvoices: () => true,
  // A call log SAVE keyed by a client-minted `clientRequestId`: the server derives the document id from (org, user, key) and
  // returns the first call log for a repeat (callLogService.createCallLog; useCreateCallLog ties the key to the content, so
  // an edited save gets a NEW key). No key => a new document per call => a retry would log the call twice: not retried.
  // A lost request was Sentry OFFICEHUB-CLIENT-69 on 2026-10-08 ('Failed to log call').
  support_createCallLog: (data) => typeof data.clientRequestId === 'string' && data.clientRequestId.length > 0,
  // Idempotent (a cache-miss path re-pulls QuickBooks and rewrites the keyed cache, so it is NOT
  // write-free), EXCEPT forceRefresh, which stamps a 5-minute rate-limit marker: a repeat after a lost
  // response would be refused with "wait before refreshing again".
  getExecutiveFinancials: (data) => !data.forceRefresh,
  // ---- Sentry OFFICEHUB-CLIENT-63 (2026-10-26 audit of the 59 events on callables that were not retried). Every entry below was read from
  // the server source on origin/main (not run); the proof is the file:line next to it. Paths are under functions/src unless named.
  // PURE READS (no write of any kind):
  //  - voice_getVoiceStatus: one users/{uid} .get() (voice/getVoiceStatus.ts:12-20).
  //  - fleet_getDriverIdentities: reads and signs image URLs; the audit is a logger line, the only .set in fleet/driverIdentity.ts is
  //    syncDriverIdentity (:116), a different function (fleet/driverIdentityCallable.ts:24-62).
  //  - fieldService_getEquipmentReview: buildEquipmentReview (forms/unitEnrichment/equipmentReview.ts:318) writes only in decide/reject
  //    (field-service/equipmentReviewCallables.ts:116-131).
  //  - fieldService_plateProofRead: documented READ-ONLY (fieldService/gallery/callables.ts:266-277); plateProofReadService.ts:152 has no
  //    Firestore write.
  voice_getVoiceStatus: () => true,
  fleet_getDriverIdentities: () => true,
  fieldService_getEquipmentReview: () => true,
  fieldService_plateProofRead: () => true,
  //  - fieldService_plateVerdictsRead: the AI plate verdict read for one unit. The handler (plateVerdictsReadHandler in
  //    functions/src/fieldService/plateVerdict/readCallable.ts) does a unit .get() (createReadIo.loadUnit in recomputeDeps.ts), a bounded
  //    plateReads query .get() (createReadIo.loadReads), photo-row reads, the pure computeUnitVerdicts, and signs photo URLs
  //    (withPhotoUrls: a local signature, stores nothing). A grep for .set/.update/.add/.delete/runTransaction/FieldValue across
  //    readCallable.ts, loadUnitReadings.ts, computeVerdicts.ts, flags.ts and signStoragePath.ts finds none; the verdicts are computed
  //    live, never written (the file header says so).
  //  - fieldService_plateVerdictFeedback: ONE vote, written by a single set() at a deterministic doc id (record() in
  //    functions/src/fieldService/plateVerdict/feedback.ts computes `feedbackDocId(uid, generatorDocId, verdict)` and calls `writeFeedback`;
  //    the writer, feedbackWriter, is `.doc(id).set(doc)`). The id is a sha256 of (uid, unit, component, field, normalized plate value)
  //    (feedbackDocId in feedbackDecision.ts, whose comment reads "A retry, a double-click or a changed mind overwrites"). A repeat rewrites
  //    the same document; it is the only write in the handler (the file header says so). If the first call landed and the repeat is refused
  //    by the server re-judge, the caller sees a refusal for a vote that is already stored: harmless.
  fieldService_plateVerdictsRead: () => true,
  fieldService_plateVerdictFeedback: () => true,
  // READS WHOSE ONLY SIDE EFFECT IS IDEMPOTENT BOOKKEEPING (the getExecutiveFinancials precedent above: idempotent rather than write-free):
  //  - drive_countFolderTree, drive_listFolderContents, listLeadDriveFiles: Drive reads. #8401 left drive_listFolderContents out because "the
  //    Drive client refreshes an expired token and UPDATES the connection document (idempotent, but a write), so it is not proven write-free".
  //    That write is exactly encryptedAccessToken + tokenExpiresAt + updatedAt from a fresh refresh (drive/driveAuth.ts:253-258,
  //    drive/driveClientForOrg.ts:98-102); the refresh token is not touched, so a second refresh only stores a newer, equally valid access
  //    token. Nothing else in the three handlers writes (drive/driveService.ts:1475+, crm/driveService.ts:573+, drive/driveFolderCounts.ts).
  //  - getGmailStatus: a probe counter capped at 30 per minute per person (gmailStatus.ts:74, gmailOAuth.ts:59) plus a Gmail profile read.
  //  - generateDailyActions: recomputes and overwrites the derived cache dailyActions/{uid} (crm/generateDailyActions.ts:573), same input,
  //    same output.
  //  - fieldService_getTravelEstimate: a rate-limited (30/min) paid route lookup whose answer is cached on the submission
  //    (field-service/travel/getTravelEstimateCallable.ts:48-52, travelEstimateService.ts:313,337); a repeat hits the cache.
  //  - getInvitationByToken: a lookup; its only write marks an expired invitation Expired (userManagement.ts:636-660), idempotent.
  // NOT here: search_searchRestricted. Its counter is 120 per minute (search/searchRateLimit.ts:5) and the search box fires every 250 ms
  // while typing, so a retry could push a fast typist over the cap: the earlier decision stands.
  drive_countFolderTree: () => true,
  drive_listFolderContents: () => true,
  listLeadDriveFiles: () => true,
  getGmailStatus: () => true,
  // forceRefresh skips the one-hour cache and recomputes the whole list (a heavier call, same precedent as getExecutiveFinancials), so only the
  // cached form is repeated.
  generateDailyActions: (d) => !d.forceRefresh,
  fieldService_getTravelEstimate: () => true,
  getInvitationByToken: () => true,
  // WRITES THAT ARE IDEMPOTENT BY CONSTRUCTION:
  //  - shared_updateLastLogin: users/{caller}.update({lastLoginAt: now}) on the caller's own document (shared-module/services/sharedService.ts:94).
  //  - shared_saveGeocodeResult: geocode_cache/{docId}.set({address, lat, lng, ...}); the same input rewrites the same document
  //    (sharedService.ts:1690).
  //  - forms_logError: appends a diagnostic row, capped at 50 per session and 200 per organization per hour
  //    (forms-module/services/formErrorLogService.ts:18-19,93); a repeat is a duplicate diagnostic row, never business data.
  shared_updateLastLogin: () => true,
  shared_saveGeocodeResult: () => true,
  forms_logError: () => true,
  // Every action is repeat-safe on the server (functions-pdf/src/functions/generateFormPdf.ts:379-387,499-509,645-652): generate and
  // download return the cached render, and regenerate REQUIRES an idempotencyKey and replays the version it already made. A regenerate
  // without a key is refused by the server; here it is not retried either.
  pdf_generateFormPdf: (data) =>
    data.action === 'generate' ||
    data.action === 'download' ||
    (data.action === 'regenerate' && typeof data.idempotencyKey === 'string' && data.idempotencyKey.length > 0),
  // WRITES THAT CARRY A CLIENT-MINTED KEY, retried ONLY when the key is present (Sentry OFFICEHUB-CLIENT-63 audit, 2026-10-26). The same body is
  // re-sent, so the server sees the same key. Each server dedupe was read from source on origin/main and has a test that goes red without it:
  //  - shared_submitInspection: the client sends `clientSubmissionId` (client/src/pages/Fleet/InspectionSubmit.tsx:222-227); the server creates
  //    the document BY that id, answers success for the same submitter with the same answers, and refuses another user or edited answers
  //    (functions/src/shared-module/services/sharedService.ts, submitInspection; tests sharedService.submitInspection.test.ts:202-224).
  //  - fieldService_createServiceTicket: the server REQUIRES a `requestId` and takes an idempotency lock keyed on org + user + requestId, with a
  //    payload fingerprint, BEFORE the AppSheet Add (field-service/services/ticketService.ts:119,328,534; test
  //    ticketAppSheetPush.test.ts:241-250 "calls addRow EXACTLY ONCE across two identical creates").
  //  - projects_createProject: a repeat of the same `requestId` finds the project the first call made and completes the checklist clone
  //    without a second project (projects/services/projectCoreService.ts:488-522; test projectCoreService.createIdempotent.test.ts:116
  //    "two clicks with the same key create ONE project and the second says replayed"). A call with NO key is not retry-safe (the server logs it).
  //  - createScheduleBlock: the client mints one `requestId` per Save click. The server takes a lock keyed on org + user + requestId BEFORE the
  //    AppSheet Add, on the inline path as well as the fast-save path (projects/scheduleBlockCreateLock.ts; wired in scheduleBlockApi.ts). A
  //    repeat of a landed save returns the SAME block with no second Add; a repeat after an Add that may have landed is REFUSED with
  //    failed-precondition; a repeat while the first is inside the Add gets `aborted`. Both are server answers (responses), which this module returns to
  //    the caller and never re-sends: it re-sends only a request LOST in transit, with the same key.
  //    Tests (scheduleBlockCreateLock.test.ts) "the repeat of the same click returns the SAME block and calls AppSheet's Add exactly once",
  //    "an ambiguous Add failure keeps the lock: the repeat is REFUSED and no second Add is sent". A call with NO key is not retry-safe.
  shared_submitInspection: (data) => typeof data.clientSubmissionId === 'string' && data.clientSubmissionId.length > 0,
  fieldService_createServiceTicket: (data) => typeof data.requestId === 'string' && data.requestId.length > 0,
  projects_createProject: (data) => typeof data.requestId === 'string' && data.requestId.length > 0,
  // The server treats a key as a key only in this shape (validBlockRequestId); any other string is KEYLESS there, so it is not retried.
  createScheduleBlock: (data) => typeof data.requestId === 'string' && /^[0-9a-zA-Z_-]{16,64}$/.test(data.requestId),
  // Sentry group fac67c12b58100d5 (3 transport failures). fieldService_galleryMarkupSave is keyed by a client-minted `mutationId` (the editor mints
  // one per save: see the gallery editor hook under client/src/hooks/fieldService/; the server REQUIRES it, functions/src/fieldService/gallery/galleryMarkupService.ts:73,85) and
  // fenced by `expectedRevision`. A repeat of a save that already committed finds lastMutationId === mutationId at revision expected+1 and answers
  // "already-applied" without writing (same file, alreadyApplied + saveMarkup; test galleryMarkupService.test.ts:166 "treats a retry of a
  // save that already committed as success, not a conflict, and writes nothing"). The same body is re-sent, so the same mutationId.
  fieldService_galleryMarkupSave: (data) => typeof data.mutationId === 'string' && data.mutationId.length > 0,
  // A SUBMIT keyed by a client-minted id (Offline-first Phase 2): a replay is accepted by design.
  // No id => server-minted => unsafe. Autosave (autosave / clientSeq) and Save Draft are excluded.
  // A status move keyed by a client-minted `mutationId` (error group e720c0e8a331cbbc, 3 lost calls 2026-10-15). Without a key a repeat after the
  // first landed is refused by the expectedStatus check with a false "this issue is now ...; refresh", so ONLY the keyed move is retried: the
  // server stores the key as `statusMutationId` on the issue and answers a repeat with success, no write
  // (functions/src/issueTriage/services/issueReportService.ts, updateIssueStatus; test issueReportService.test.ts "a repeat ...").
  issueTriage_updateStatus: (data) => typeof data.mutationId === 'string' && data.mutationId.length > 0,
  forms_createSubmission: (data) =>
    typeof data.submissionId === 'string' &&
    data.submissionId.length > 0 &&
    !data.autosave &&
    data.clientSeq === undefined &&
    (data.status === undefined || data.status === 'submitted'),
  // Two forms writes whose replay is proven harmless (OFFICEHUB-CLIENT-63; ORCH-109 ruling 2026-11-02):
  //  - forms_appendSubmissionPhoto: the photo is placed by its own id. A replay leaves the content unchanged: it finds the id already in
  //    its slot ("already-attached", nothing written) or merges onto the same id ("merged"; updatedAt/updatedBy are re-stamped)
  //    (appendPhotoToField, services/formSubmissionService.ts; red test "is idempotent under retry" in formSubmissionService.test.ts).
  //    Only keyed calls are retried: the server refuses a photo with no id. Named limits: a replay that lands after the report was
  //    locked is refused, the same answer the offline photo drain already gets; and a replay that lands after the user deleted that
  //    photo in between (a few seconds, up to the 40 s budget) would bring it back, the same race the drain already has.
  //  - forms_updateSubmissionFormPdfMeta WITHOUT a milestone (or "current"): a plain overwrite of fixed values (url, path, version), so a
  //    replay leaves the same document. With milestone "submitted"/"approved" it also adds a pdfVersions entry stamped with the server
  //    clock (arrayUnion of an object holding new Date()), so a replay would list the PDF twice: those are attempted once.
  // forms_updateSubmission is deliberately NOT here: a Save Draft that landed would bump explicitSaveCount again, a submit that landed is
  // answered "can no longer be edited" on the repeat, and a discard is answered "only an open draft". It carries no request key.
  forms_appendSubmissionPhoto: (data) => {
    const photo = data.photo as { id?: unknown } | null | undefined;
    return typeof photo === 'object' && photo !== null && typeof photo.id === 'string' && photo.id.length > 0;
  },
  forms_updateSubmissionFormPdfMeta: (data) => data.milestone === undefined || data.milestone === 'current',
};

/** The callable names that may be retried; a test pins this set so an addition needs its server-side proof (see the comments above). */
export const RETRY_SAFE_CALLABLES: readonly string[] = Object.keys(RETRY_SAFE);

export interface CallableCall {
  readonly fn: string;
  readonly data: CallableData;
}

/** Name and payload of a callable POST, or null for anything this module does not handle. */
export function parseCallableCall(input: RequestInfo | URL, init?: RequestInit): CallableCall | null {
  if (init?.method?.toUpperCase() !== 'POST' || typeof init.body !== 'string') return null;
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : null;
  const match = url ? CALLABLE_URL_RE.exec(url) : null;
  if (!match) return null;
  try {
    const parsed: unknown = JSON.parse(init.body);
    const data = (parsed as { data?: unknown } | null)?.data;
    if (data === null || typeof data !== 'object' || Array.isArray(data)) return null;
    return { fn: match[1], data: data as CallableData };
  } catch {
    return null;
  }
}

function isRetrySafe(call: CallableCall | null): boolean {
  return call !== null && Object.prototype.hasOwnProperty.call(RETRY_SAFE, call.fn) && RETRY_SAFE[call.fn](call.data);
}

/** First retry after ~0.4s, second after ~1.2s, plus up to 25% jitter. */
const BACKOFF_MS = [400, 1200] as const;
const MAX_JITTER = 0.25;
/**
 * Total time, from the first attempt, after which no further retry STARTS. The Firebase SDK gives the
 * caller up after 70s but passes this loop no signal, so without a budget a loop still running after
 * the caller was told "deadline-exceeded" could send a late copy of an old payload.
 */
export const RETRY_BUDGET_MS = 40_000;

export interface CallableRetryDeps {
  readonly sleep?: (ms: number) => Promise<void>;
  readonly random?: () => number;
  readonly isOnline?: () => boolean;
  readonly now?: () => number;
  /** Called after each attempt that failed with no response and WILL be retried (1-based). */
  readonly onAttemptFailed?: (url: string, attempt: number, error: unknown) => void;
  /** Called once when a hedged call that sent a second attempt settles (see HedgeOutcome). */
  readonly onHedgeOutcome?: (outcome: HedgeOutcome) => void;
}

/**
 * How a hedged call that needed a second attempt ended, for the post-deploy measurement: which attempt answered (null = neither, the
 * caller got an error) and how long after the first attempt started. `trigger` says why attempt 2 was sent: attempt 1 was still silent
 * at HEDGE_AFTER_MS ('slow'), or it failed with no response ('lost').
 */
export interface HedgeOutcome {
  readonly url: string;
  readonly callable: string;
  readonly answeredBy: 1 | 2 | null;
  readonly elapsedMs: number;
  readonly trigger: 'slow' | 'lost';
}

const defaultSleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));
const defaultOnline = (): boolean => typeof navigator === 'undefined' || navigator.onLine !== false;

function isAbort(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === 'AbortError';
}

/**
 * `fetchImpl(input, init)` with up to two retries when a retry-safe callable gets no response.
 * A HEDGED_CALLABLES call goes through fetchWithHedge instead (at most two attempts).
 * Anything else behaves exactly like a plain `fetchImpl(input, init)`.
 */
export async function fetchWithCallableRetry(
  fetchImpl: typeof fetch,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  deps: CallableRetryDeps = {}
): Promise<Response> {
  const call = parseCallableCall(input, init);
  if (!isRetrySafe(call)) return fetchImpl(input, init);
  if (call !== null && HEDGED.has(call.fn)) return fetchWithHedge(fetchImpl, input, init, call.fn, deps);

  const { sleep = defaultSleep, random = Math.random, isOnline = defaultOnline, now = Date.now, onAttemptFailed } = deps;
  const startedAt = now();
  const url = typeof input === 'string' ? input : String(input);

  for (let attempt = 1; ; attempt += 1) {
    try {
      return await fetchImpl(input, init);
    } catch (error) {
      const retriesLeft = attempt <= BACKOFF_MS.length;
      if (!retriesLeft || isAbort(error) || init?.signal?.aborted || !isOnline()) throw error;
      const delay = BACKOFF_MS[attempt - 1] * (1 + random() * MAX_JITTER);
      if (now() - startedAt + delay > RETRY_BUDGET_MS) throw error;
      try {
        onAttemptFailed?.(url, attempt, error);
      } catch {
        // A reporting callback must never stop the retry or replace the network error.
      }
      await sleep(delay);
    }
  }
}

/**
 * Callables whose slow first attempt is hedged. A subset of RETRY_SAFE (a test pins both facts): the QuickBooks read family above, which
 * writes nothing, so two copies running on the server at once is harmless. Writes are never hedged, keyed or not: a second copy can run
 * WHILE the first is still running, which the keyed dedupes above were not proven against.
 */
export const HEDGED_CALLABLES: readonly string[] = [
  'support_getRelatedBilling',
  'finance_qboRead',
  'projects_qboRead',
  'fieldService_qboRead',
  'dataHub_readQboTable',
  'customers_qboRead',
];
const HEDGED: ReadonlySet<string> = new Set(HEDGED_CALLABLES);

/** A hedged call never sends more than this many copies, whatever triggered the second one. */
const MAX_HEDGED_ATTEMPTS = 2;
/** When a silent first attempt gets its fresh twin. */
export const HEDGE_AFTER_MS = 25_000;
/**
 * The whole call, from the first attempt: the Firebase SDK's default callable timeout (70 s), which none of the hedged callers override.
 * The SDK gives the caller up then but passes no signal, so here every attempt still open is aborted and the promise rejects.
 */
export const CALL_BUDGET_MS = 70_000;

const CALL_BUDGET_ERROR_NAME = 'CallBudgetExceededError';

class CallBudgetExceededError extends Error {
  constructor() {
    super(`No answer within ${CALL_BUDGET_MS} ms`);
    this.name = CALL_BUDGET_ERROR_NAME;
  }
}

/**
 * True for the rejection a hedged call gives at CALL_BUDGET_MS. The SDK has already told the caller "deadline-exceeded" by then, so a
 * fetch wrapper must not report this as a second, status-0 failure of its own.
 */
export function isCallBudgetExceeded(error: unknown): boolean {
  return typeof error === 'object' && error !== null && (error as { name?: unknown }).name === CALL_BUDGET_ERROR_NAME;
}

/**
 * Statuses the Cloud Run front end gives on its own while instances are starting or full (no instance, over capacity, upstream
 * timeout). While the OTHER attempt is still open such an answer is held back, not returned: the other attempt may still bring the
 * real answer. It is returned only if nothing better arrives.
 */
const GATEWAY_STATUSES: ReadonlySet<number> = new Set([429, 502, 503, 504]);

/**
 * WHY (error group f8f7e3503c27e2cb, measured 2026-10-29 from Cloud Run logs): 7 of its 22 "deadline-exceeded" events are these reads
 * (finance_qboRead 6, support_getRelatedBilling 1), all on a COLD BOOT of the default codebase (finance-qboread on 10-28: 19 starts,
 * p50 41.7 s, max 209 s; the server work itself 0.5-0.9 s). A request that lands on a slow-booting instance stays PINNED to it: on
 * 10-28 a request waited 207.9 s on one instance while another, started later, was ready in 20 s and served 7 requests (same shape in 3
 * of 3 windows). So a fresh request after ~25 s is usually answered at once, where waiting on the first one runs into the 70 s timeout.
 *
 * HOW. Attempt 1 starts. Attempt 2 starts when attempt 1 is still silent at HEDGE_AFTER_MS (a one-shot check: a device offline at that
 * moment gets no twin), or, after the first backoff, when attempt 1 failed with no response. Never more than MAX_HEDGED_ATTEMPTS; the
 * first answer wins (a GATEWAY_STATUSES answer only once nothing else is open), the other attempt is aborted and anything it does
 * later is ignored, so the caller sees exactly one result. An attempt that fails while the other is still open is not an error yet,
 * unless the device reports offline (the offline queue owns that case). At CALL_BUDGET_MS everything still open is aborted. A caller
 * abort before the answer aborts every attempt; the listener is removed when the call settles, so a long-lived signal does not collect
 * one per call.
 *
 * Named limits: compared with the other listed callables these reads give up one status-0 retry (two attempts, not three), and an
 * attempt 2 sent because attempt 1 was LOST gets no twin of its own, so if it lands on a slow-booting instance it waits as before.
 * A caller abort AFTER the answer no longer cancels the winner's body read, as it would on a plain fetch with that signal (no hedged
 * caller passes a signal today: the Firebase SDK sends none).
 */
function fetchWithHedge(
  fetchImpl: typeof fetch,
  input: RequestInfo | URL,
  init: RequestInit | undefined,
  callable: string,
  deps: CallableRetryDeps
): Promise<Response> {
  const { sleep = defaultSleep, random = Math.random, isOnline = defaultOnline, now = Date.now, onAttemptFailed, onHedgeOutcome } = deps;
  const url = typeof input === 'string' ? input : String(input);
  const callerSignal = init?.signal ?? undefined;
  const startedAt = now();

  return new Promise<Response>((resolve, reject) => {
    const controllers: AbortController[] = [];
    let settled = false;
    let running = 0;
    let trigger: HedgeOutcome['trigger'] | null = null;
    let heldBack: { readonly response: Response; readonly attempt: 1 | 2; readonly controller: AbortController } | null = null;
    let hedgeTimer: ReturnType<typeof setTimeout> | undefined;
    let budgetTimer: ReturnType<typeof setTimeout> | undefined;

    const report = (answeredBy: HedgeOutcome['answeredBy']): void => {
      if (trigger === null) return;
      try {
        onHedgeOutcome?.({ url, callable, answeredBy, elapsedMs: now() - startedAt, trigger });
      } catch {
        // A reporting callback must never change what the caller gets.
      }
    };
    /** Settles once; aborts every attempt but `keep` (the winner, whose body the caller still reads). */
    const finish = (settle: () => void, keep?: AbortController): void => {
      if (settled) return;
      settled = true;
      clearTimeout(hedgeTimer);
      clearTimeout(budgetTimer);
      callerSignal?.removeEventListener('abort', onCallerAbort);
      controllers.forEach((c) => c !== keep && c.abort());
      settle();
    };
    const succeed = (response: Response, attempt: 1 | 2, controller: AbortController): void => {
      finish(() => {
        report(attempt);
        resolve(response);
      }, controller);
    };
    /** No answer to give: a held-back gateway answer is still better than an error. */
    const fail = (error: unknown): void => {
      if (heldBack !== null) {
        succeed(heldBack.response, heldBack.attempt, heldBack.controller);
        return;
      }
      finish(() => {
        report(null);
        reject(error);
      });
    };

    const launch = (why: HedgeOutcome['trigger'] | null): void => {
      if (settled || controllers.length >= MAX_HEDGED_ATTEMPTS) return;
      if (why !== null) trigger = why;
      const controller = new AbortController();
      controllers.push(controller);
      const attempt = controllers.length as 1 | 2;
      running += 1;
      let pending: Promise<Response>;
      try {
        pending = fetchImpl(input, { ...init, signal: controller.signal });
      } catch (error) {
        pending = Promise.reject(error);
      }
      pending.then(
        (response) => {
          running -= 1;
          // No settled check here on purpose: finish() is the one place that makes a late answer a no-op.
          if (running > 0 && GATEWAY_STATUSES.has(response.status)) {
            heldBack = heldBack ?? { response, attempt, controller };
            return;
          }
          succeed(response, attempt, controller);
        },
        (error: unknown) => {
          running -= 1;
          if (settled) return;
          const retriable = !isAbort(error) && !callerSignal?.aborted && isOnline();
          if (running > 0 && retriable) return; // the other attempt may still answer
          if (controllers.length >= MAX_HEDGED_ATTEMPTS || !retriable) {
            fail(error);
            return;
          }
          clearTimeout(hedgeTimer);
          const delay = BACKOFF_MS[0] * (1 + random() * MAX_JITTER);
          if (now() - startedAt + delay > RETRY_BUDGET_MS) {
            fail(error);
            return;
          }
          try {
            onAttemptFailed?.(url, attempt, error);
          } catch {
            // A reporting callback must never stop the retry or replace the network error.
          }
          void sleep(delay).then(() => {
            if (settled) return;
            if (callerSignal?.aborted || !isOnline()) fail(error);
            else launch('lost');
          });
        }
      );
    };

    // A cancel is not a server non-answer: it settles without a hedge outcome, so the post-deploy measurement counts only real ones.
    function onCallerAbort(): void {
      const reason: unknown = callerSignal?.reason;
      finish(() => reject(reason instanceof Error ? reason : new DOMException('The operation was aborted.', 'AbortError')));
    }
    if (callerSignal?.aborted) {
      onCallerAbort();
      return;
    }
    callerSignal?.addEventListener('abort', onCallerAbort, { once: true });
    hedgeTimer = setTimeout(() => {
      if (!settled && running > 0 && isOnline()) launch('slow');
    }, HEDGE_AFTER_MS);
    budgetTimer = setTimeout(() => fail(new CallBudgetExceededError()), CALL_BUDGET_MS);
    launch(null);
  });
}
